export type PartnerPathId = 'pilot' | 'build' | 'visibility' | 'managed'

export interface PartnerPath {
  id: PartnerPathId
  index: string
  tab: string
  title: string
  summary: string
  question: string
  inputs: readonly string[]
  checks: readonly string[]
  output: string
  outcome: string
  deliverables: readonly string[]
  partnerRole: string
}

export const PARTNER_PATHS: readonly PartnerPath[] = [
  {
    id: 'pilot',
    index: '01',
    tab: 'Pilot audit',
    title: 'Pilot, audit, and scope',
    summary:
      'Start with one client, one domain, and one clear decision. We establish the baseline, identify the highest-value fixes, and turn the evidence into an implementation brief.',
    question: 'What can we prove before the larger investment?',
    inputs: [
      'Client brief',
      'Live domain',
      'Brand context',
      'Current evidence',
    ],
    checks: ['Baseline measured', 'Priorities ranked', 'Scope approved'],
    output: 'Partner-branded audit and implementation brief',
    outcome: 'A low-risk first move your team can explain and sell.',
    deliverables: [
      'Radar baseline with expert interpretation',
      'Prioritized technical and content findings',
      'Implementation brief with decisions and dependencies',
      'Private scope recommendation for the next phase',
    ],
    partnerRole:
      'You provide the client context and approve the presentation. We prepare the evidence and delivery plan behind the scenes.',
  },
  {
    id: 'build',
    index: '02',
    tab: 'Website build',
    title: 'Website or application build',
    summary:
      'Bring an approved brief or ask us to shape it with you. Pixelmojo handles experience design, engineering, integrations, launch, documentation, and handoff.',
    question: 'What must the client be able to do at launch?',
    inputs: [
      'Approved scope',
      'Content and access',
      'Brand system',
      'Success criteria',
    ],
    checks: ['UX resolved', 'Build reviewed', 'Launch ready'],
    output: 'Production website or application',
    outcome: 'A working system delivered through your client relationship.',
    deliverables: [
      'Responsive experience design and production engineering',
      'CMS, analytics, and agreed business integrations',
      'Reviewable checkpoints with partner approval',
      'Launch support, documentation, and defined handoff',
    ],
    partnerRole:
      'You own the relationship and consolidate feedback. We own the agreed build scope, technical decisions, and delivery checkpoints.',
  },
  {
    id: 'visibility',
    index: '03',
    tab: 'Visibility layer',
    title: 'AI visibility implementation',
    summary:
      "Make the client's identity, priority answers, and crawl access explicit across the site. This can attach to a new build or improve an existing site.",
    question: 'Can answer engines identify, read, and cite the brand?',
    inputs: ['Entity facts', 'Site access', 'Priority pages', 'Radar baseline'],
    checks: ['Crawl path clear', 'Entity graph aligned', 'Answers structured'],
    output: 'Implemented AI visibility foundation',
    outcome: 'A site that states clearly who the client is and what it knows.',
    deliverables: [
      'Crawler configuration and llms.txt implementation',
      'Structured data and entity clarity',
      'Answer-first page improvements',
      'Radar baseline plus a prioritized action plan',
    ],
    partnerRole:
      'You approve brand facts and client-facing language. We implement the agreed technical and content layer.',
  },
  {
    id: 'managed',
    index: '04',
    tab: 'Monitoring',
    title: 'Managed monitoring and implementation',
    summary:
      'Measure what changes, interpret the signal, and ship the approved next actions at an agreed cadence. Scope and implementation limits are defined in the statement of work.',
    question: 'What changed, what matters, and what ships next?',
    inputs: [
      'Tracked prompts',
      'Priority entities',
      'Reporting cadence',
      'Approved scope',
    ],
    checks: ['Signals compared', 'Actions approved', 'Work documented'],
    output: 'Recurring evidence and bounded implementation',
    outcome: 'A repeatable operating rhythm without an open-ended retainer.',
    deliverables: [
      'Agreed monitoring cadence and evidence review',
      'Partner-branded reporting and recommendations',
      'Approved implementation within the contracted scope',
      'Decision log and next-period priorities',
    ],
    partnerRole:
      'You choose what reaches the client and approve the next actions. We measure, interpret, and implement within the agreed limits.',
  },
] as const

export const PARTNER_PROBLEMS = [
  {
    id: 'asked',
    index: '01',
    title: 'The client asked about AI visibility.',
    signal:
      'They want a credible answer now, but your team does not need another software subscription or a new specialist department.',
    recommendation: 'Start with Pilot, audit, and scope.',
    pathId: 'pilot' as const,
  },
  {
    id: 'won',
    index: '02',
    title: 'You won the strategy. Now it has to ship.',
    signal:
      'The brief is approved, but the delivery team is overloaded or the build needs deeper product and engineering capability.',
    recommendation: 'Move into Website or application build.',
    pathId: 'build' as const,
  },
  {
    id: 'invisible',
    index: '03',
    title: 'You need capacity that stays behind the brand.',
    signal:
      'The client relationship is yours. You need a senior execution team that works through your approvals and does not compete for the account.',
    recommendation: 'Use the partner model across any delivery path.',
    pathId: 'visibility' as const,
  },
  {
    id: 'ongoing',
    index: '04',
    title: 'The client needs evidence after launch.',
    signal:
      'A one-time build is not enough. The team needs a measured cadence for monitoring, decisions, and approved improvements.',
    recommendation: 'Add Managed monitoring and implementation.',
    pathId: 'managed' as const,
  },
] as const

export const BUILD_SCOPES = [
  {
    index: '01 / Essential',
    title: 'A focused website that makes the offer clear.',
    scope:
      'Five to eight responsive pages, light content editing, analytics, launch setup, documentation, and a defined post-launch support window.',
    window:
      'Example planning range: 3 to 4 weeks after kickoff, complete content, required access, and approvals.',
    bestFor: 'New offers, repositioning, and focused campaigns.',
  },
  {
    index: '02 / Growth',
    title: 'A complete commercial site built to evolve.',
    scope:
      'Ten to twenty pages, full CMS, custom components, agreed business integrations, analytics, documentation, and launch support.',
    window:
      'Example planning range: 6 to 8 weeks after kickoff, complete content, required access, and approvals.',
    bestFor: 'B2B teams with multiple audiences, offers, or markets.',
  },
  {
    index: '03 / Scale',
    title: 'A custom platform for complex products or operations.',
    scope:
      'Discovery-led architecture for authentication, backend systems, databases, integrations, multi-sided workflows, and operational handoff.',
    window:
      'Custom timeline confirmed after discovery, dependencies, security needs, and approval cadence are known.',
    bestFor:
      'Multi-sided products, complex workflows, and connected operations.',
  },
] as const

export const PROOF_ITEMS = [
  {
    href: '/projects/lakbay-ai',
    meta: 'Travel technology / AI platform',
    stat: '18 / 3',
    statLabel: 'mapped destinations / connected portals',
    title: 'One travel idea became a working multi-sided product.',
    copy: 'Lakbay connects traveler planning, agent workflows, and administration. Its published interface generates a tailored route in under 60 seconds.',
  },
  {
    href: '/projects/seo-intelligence-platform',
    meta: 'Marketing technology / Demonstrated visibility workspace',
    stat: '5 / 12',
    statLabel: 'demonstrated AI surfaces / synthetic queries',
    title: 'Search and citation signals were modeled in one operating view.',
    copy: 'The published prototype uses a 12-query synthetic dataset to demonstrate five AI surfaces and 7-, 30-, and 90-day mock views. It is not a live Search Console connection.',
  },
  {
    href: '/projects/logistics-track-trace-system',
    meta: 'Enterprise logistics / Operations',
    stat: '1 / Demo / 6',
    statLabel: 'record / demonstrated exception view / technologies',
    title: 'Fragmented events became one inspectable shipment record.',
    copy: 'The case study demonstrates normalized handoffs and an exception-first interface using a fixed synthetic dataset. It is not connected to a live account.',
  },
] as const

export const METHOD_STEPS = [
  {
    index: '01 / Frame',
    title: 'Name the client decision.',
    copy: 'Align on the audience, constraint, commercial result, evidence standard, and approval path before production begins.',
    outputs: ['Shared brief', 'Success measures', 'Guardrails'],
  },
  {
    index: '02 / Prove',
    title: 'Test the risky assumption.',
    copy: 'Use an audit, prototype, or live signal to challenge the highest-risk assumption before the scope expands.',
    outputs: ['Evidence pack', 'Prototype or baseline', 'Go or no-go'],
  },
  {
    index: '03 / Build',
    title: 'Ship through partner checkpoints.',
    copy: 'Turn the approved direction into a production system with visible increments, consolidated feedback, and clear decisions.',
    outputs: ['Working increments', 'Decision log', 'Production release'],
  },
  {
    index: '04 / Transfer',
    title: 'Hand off without account conflict.',
    copy: 'Deliver the agreed custom work, documentation, and operating knowledge through the partner relationship.',
    outputs: ['Defined handoff', 'Documentation', 'Optional scoped support'],
  },
] as const

export const PARTNER_COMMITMENTS = [
  {
    index: '01',
    title: 'Your relationship stays yours.',
    copy: 'Pixelmojo does not contact the end client unless you invite us into the conversation.',
  },
  {
    index: '02',
    title: 'Your brand stays in front.',
    copy: 'Agreed reports, documentation, and client-facing assets can be prepared under your brand. No public attribution or case study without written approval.',
  },
  {
    index: '03',
    title: 'Ownership is explicit.',
    copy: 'Project-specific custom code transfers after full payment. Pixelmojo pre-existing tools, including Radar and other platform IP, open-source and third-party licenses, services, and licensed assets remain under their respective terms.',
  },
  {
    index: '04',
    title: 'Scope stays bounded.',
    copy: 'Monitoring cadence, implementation limits, responsibilities, approvals, and support are written into the statement of work before delivery begins.',
  },
] as const
